import { Controller } from "@hotwired/stimulus"

// Advisor intro availability board: expand an advisor's row to see their open times,
// expand/collapse every row at once, filter the rows by advisor name, and focus a single
// day (click its column header) to read every advisor's times for that day.
export default class extends Controller {
  static targets = ["advisor", "search", "noMatch", "toggleAllLabel", "dayHead", "focusChip", "focusLabel"]

  connect() {
    this.focusedDay = null
  }

  // --- Advisor rows ---

  toggle(event) {
    this.setOpen(event.currentTarget.closest(".ia-advisor"), null)
    this.syncToggleAllLabel()
  }

  toggleWithKey(event) {
    if (!this.isActivationKey(event)) return

    event.preventDefault()
    this.toggle(event)
  }

  toggleAll() {
    this.clearFocus()
    const expandable = this.visibleExpandableAdvisors()
    const open = !expandable.every((advisor) => advisor.classList.contains("is-open"))
    expandable.forEach((advisor) => this.setOpen(advisor, open))
    this.syncToggleAllLabel()
  }

  filter() {
    const query = this.searchTarget.value.trim().toLowerCase()
    let matches = 0

    this.advisorTargets.forEach((advisor) => {
      const match = query === "" || advisor.dataset.name.includes(query)
      advisor.classList.toggle("d-none", !match)
      if (match) matches += 1
    })

    this.noMatchTarget.classList.toggle("d-none", matches > 0)
    this.syncToggleAllLabel()
  }

  // --- Day focus ---

  focusDay(event) {
    const index = Number(event.currentTarget.dataset.dayIndex)
    if (index === this.focusedDay) {
      this.clearFocus()
    } else {
      this.applyFocus(index)
    }
  }

  focusDayWithKey(event) {
    if (!this.isActivationKey(event)) return

    event.preventDefault()
    this.focusDay(event)
  }

  clearFocus() {
    if (this.focusedDay === null) return

    this.focusedDay = null
    this.element.classList.remove("is-day-focused")
    this.markFocused(null)
    this.advisorTargets.forEach((advisor) => advisor.classList.remove("is-day-empty"))
    this.reorder((a, b) => this.originalIndex(a) - this.originalIndex(b))
    this.focusChipTarget.classList.add("d-none")
  }

  applyFocus(index) {
    this.rememberOriginalOrder()
    this.focusedDay = index
    this.element.classList.add("is-day-focused")
    this.markFocused(index)

    this.advisorTargets.forEach((advisor) => {
      advisor.classList.toggle("is-day-empty", this.countFor(advisor, index) === 0)
    })
    // Most openings that day first; ties keep the board's normal order.
    this.reorder((a, b) => this.countFor(b, index) - this.countFor(a, index) || this.originalIndex(a) - this.originalIndex(b))

    const head = this.dayHeadTargets[index]
    this.focusLabelTarget.textContent = `${head.dataset.dayLabel} · ${head.querySelector(".ia-day-total").textContent} open`
    this.focusChipTarget.classList.remove("d-none")
  }

  // Flags the focused column's header, heatmap cells, and schedule cards.
  markFocused(index) {
    this.dayHeadTargets.forEach((head, i) => head.setAttribute("aria-pressed", String(i === index)))
    this.element.querySelectorAll("[data-day-index]").forEach((el) => {
      el.classList.toggle("is-focused", Number(el.dataset.dayIndex) === index)
    })
  }

  // --- Helpers ---

  countFor(advisor, index) {
    return Number(advisor.dataset.dayCounts.split(",")[index])
  }

  rememberOriginalOrder() {
    this.advisorTargets.forEach((advisor, i) => {
      if (advisor.dataset.originalIndex === undefined) advisor.dataset.originalIndex = i
    })
  }

  originalIndex(advisor) {
    return Number(advisor.dataset.originalIndex)
  }

  // Re-appends advisor rows in sorted order, just ahead of the "no match" message.
  reorder(compare) {
    const sorted = [...this.advisorTargets].sort(compare)
    sorted.forEach((advisor) => this.noMatchTarget.before(advisor))
  }

  // open: true/false to force a state, null to flip it.
  setOpen(advisor, open) {
    const row = advisor.querySelector(".ia-advisor-row.is-expandable")
    if (!row) return

    const isOpen = open === null
      ? advisor.classList.toggle("is-open")
      : advisor.classList.toggle("is-open", open)
    row.setAttribute("aria-expanded", String(isOpen))
  }

  visibleExpandableAdvisors() {
    return this.advisorTargets.filter(
      (advisor) => !advisor.classList.contains("d-none") && advisor.querySelector(".ia-advisor-row.is-expandable")
    )
  }

  syncToggleAllLabel() {
    const expandable = this.visibleExpandableAdvisors()
    const allOpen = expandable.length > 0 && expandable.every((advisor) => advisor.classList.contains("is-open"))
    this.toggleAllLabelTarget.textContent = allOpen ? "Collapse all" : "Expand all"
  }

  isActivationKey(event) {
    return event.key === "Enter" || event.key === " "
  }
}
